import React, { useState } from "react";
import { ShoppingCart, Video, X, Zap } from "lucide-react";
import OptionCard from "./OptionCard";
import ComingSoonView from "./ComingSoonView";
import { useNavigate } from "react-router-dom"; // ✅ Import router navigation

export type ModalOption = "marketplace" | "video" | "";

interface SelectModalProps {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
}

// Custom Sparkles icon
const SparklesIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
  </svg>
);

const SelectModal: React.FC<SelectModalProps> = ({ isOpen, setOpen }) => {
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ModalOption>("");
  const isProd = import.meta.env.VITE_NODE_ENV === "production";
  const url = isProd
    ? "https://passionbits.io/dashboard/explore-creators"
    : "https://stage.passionbits.io/dashboard/explore-creators";

  const handleOptionClick = (option: ModalOption) => {
    if (option === "marketplace") {
      window.location.href = url;
      setOpen(false);
      return;
    }

    setSelectedOption(option);
    setShowComingSoon(true);
  };

  const handleClose = () => {
    setOpen(false);
    setShowComingSoon(false);
    setSelectedOption("");
  };

  const handleBackToOptions = () => {
    setShowComingSoon(false);
    setSelectedOption("");
  };

  if (!isOpen) return null;

  return (
    <div>
      {!showComingSoon ? (
        <div className="p-5">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Choose Your Action
              </h2>
              <p className="text-gray-600 text-sm">
                Select what you'd like to do with your generated content
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Marketplace Option */}
            <OptionCard
              title="Create Marketplace Order"
              description="Convert your script into a marketplace order for professional content creation"
              icon={<ShoppingCart className="w-8 h-8 text-white" />}
              features={["One-click setup", "Pro creators"]}
              colors={{
                bgFrom: "from-orange-50",
                bgTo: "to-red-50",
                border: "border-orange-200",
                hoverFrom: "from-orange-100",
                hoverTo: "to-red-100",
                featureDots: ["bg-orange-500", "bg-red-500"],
              }}
              onClick={() => handleOptionClick("marketplace")}
              badgeIcon={<SparklesIcon className="w-3 h-3 text-white" />}
            />

            {/* Video Option */}
            <OptionCard
              title="Create Video via AI"
              description="Generate professional videos automatically using advanced AI technology"
              icon={<Video className="w-8 h-8 text-white" />}
              features={["AI-powered", "Instant results"]}
              colors={{
                bgFrom: "from-blue-50",
                bgTo: "to-purple-50",
                border: "border-blue-200",
                hoverFrom: "from-blue-100",
                hoverTo: "to-purple-100",
                featureDots: ["bg-blue-500", "bg-purple-500"],
              }}
              onClick={() => handleOptionClick("video")}
              badgeIcon={<Zap className="w-3 h-3 text-white" />}
            />
          </div>
        </div>
      ) : (
        <ComingSoonView
          selectedOption={selectedOption}
          onBack={handleBackToOptions}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

export default SelectModal;
